/**
 * The pieces the share modal and the default-sharing setting have in common.
 *
 * Both ask the same question — who may see this, and at what level — so they
 * render the same picker, the same access list and the same general-access
 * control. They differ in what they are choosing for: the share modal edits one
 * entity and writes on every change, while the setting edits a default for
 * notes that do not exist yet and writes once, on Save (#11877).
 */

export type GeneralAccess = "restricted" | "organization" | "link";

/** What the picker needs to offer someone: enough to show and identify them. */
export interface PickableUser {
  id: string;
  name: string;
  email?: string | null;
}

export interface PickableTeam {
  id: string;
  name: string;
}

export interface AccessItem {
  id: string;
  name: string;
  email?: string;
  /** "organization" only for access inherited from a view group (#13896):
   *  everyone in the organization, through the group. */
  kind: "user" | "team" | "organization";
  role: "Owner" | "Editor" | "Viewer";
  permissionType: "view" | "edit";
  /**
   * Set when the access comes through another entity (the agent that
   * produced an artifact, #11941; a view group that holds a view, #13896):
   * named in the row, and neither the role nor the removal is offered, since
   * it is not this entity's grant.
   */
  inheritedFrom?: string;
  /** What `inheritedFrom` is: an agent reads "via <agent>", a view group
   *  or an artifact's folder (#14320) "<Level>, from <group or folder>". */
  inheritedKind?: "agent" | "group" | "folder";
  /**
   * A member added but not yet saved (a request for access, round 10 U4): the row says so until
   * Save sends the grant.
   */
  unsaved?: boolean;
}

/** Two initials for an avatar, from whatever the name gives us. */
export const getInitials = (name: string): string =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
